import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { http } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import { Card, SectionTitle, SeverityChip, StatusBadge, KevBadge, Spinner } from "@/components/Kit";

export default function Exposure() {
  const { orgId } = useOrg();
  const navigate = useNavigate();
  const [cve, setCve] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);

  const lookup = async (e) => {
    e?.preventDefault();
    if (!cve.trim()) return;
    setBusy(true); setRes(null);
    try {
      const { data } = await http.get(`/exposure/${cve.trim().toUpperCase()}`, { params: { org_id: orgId } });
      setRes(data);
    } finally { setBusy(false); }
  };

  return (
    <div className="p-6 max-w-[1000px] mx-auto fade-up">
      <h1 className="font-display text-3xl font-bold text-[#A5300F]">Exposure Lookup</h1>
      <p className="text-sm text-[#8A8175] mt-1 mb-5">Enter a CVE identifier to see every application affected across this organisation.</p>

      <form onSubmit={lookup} className="flex gap-2 mb-6">
        <input value={cve} onChange={(e) => setCve(e.target.value)} placeholder="CVE-2021-23337" data-testid="cve-input"
          className="flex-1 bg-white border border-[#E7D9C6] rounded-md px-4 py-2.5 text-sm font-mono focus:outline-none focus:border-[#A5300F]" />
        <button type="submit" disabled={busy} data-testid="cve-lookup-btn"
          className="flex items-center gap-2 bg-[#A5300F] text-white font-semibold px-5 rounded-md hover:bg-[#8F2A0D] disabled:opacity-50">
          {busy ? <Spinner /> : <Search className="w-4 h-4" />} Look up
        </button>
      </form>

      {res && (
        <Card className="p-5">
          <div className="flex items-center gap-3 mb-2">
            <SectionTitle>{res.cve_id}</SectionTitle>
            {res.kev && <KevBadge />}
            {res.cvss != null && <span className="text-sm text-[#8A8175]">CVSS {res.cvss}</span>}
          </div>
          {res.description && <p className="text-sm text-[#4a453d] mb-4">{res.description}</p>}
          <div className="text-[11px] uppercase text-[#8A8175] font-semibold mb-2">
            {res.affected.length} affected finding(s)
          </div>
          <table className="w-full text-sm">
            <thead><tr className="text-[11px] uppercase tracking-wider text-[#8A8175] text-left border-b border-[#ECE6DD]">
              <th className="py-2">Application</th><th>Package</th><th>Version</th><th>Fixed</th><th>Severity</th><th>Status</th>
            </tr></thead>
            <tbody>
              {res.affected.map((a, i) => (
                <tr key={`${a.app_id}-${i}`} onClick={() => navigate(`/applications/${a.app_id}`)}
                  className="border-b border-[#F4EFE8] hover:bg-[#FBF7F1] cursor-pointer">
                  <td className="py-2 font-semibold">{a.application}</td>
                  <td className="font-mono text-xs">{a.package_name || "—"}</td>
                  <td className="font-mono text-xs">{a.package_version || "—"}</td>
                  <td className="font-mono text-xs text-[#2E7D32]">{a.fixed_version || "—"}</td>
                  <td><SeverityChip severity={a.severity} small /></td>
                  <td><StatusBadge status={a.status} /></td>
                </tr>
              ))}
              {res.affected.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-[#B5AC9E]">No applications exposed to this CVE.</td></tr>}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
